import classNames from "classnames";
import React from "react";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { Avatar, LazyLoadImage, Link } from "../../../misc";
import { DraggingContext } from "../../../utils";

import "./BlogCard.scss";

interface Props {
  content: IContent;
  className: string;
  heroImageUrl: string;
  heroImageMeta: string;
  renderTags: (content: IContent) => JSX.Element;
  delayClassIndex: number;
  isHero: boolean;
  tabIndex?: number;
  onClick?: () => void;
  url: string;
}

/**
 * A card to display a blog entry with a link to the entry's page.
 *
 * @status stable
 */
export const BlogCard = ({
  content,
  className,
  heroImageUrl,
  heroImageMeta,
  renderTags,
  delayClassIndex,
  isHero,
  onClick,
  tabIndex,
  url
}: Props) => {
  if (!content) {
    return null;
  }

  const authorName = content?.author
    ? `${content.author.firstName} ${content.author.lastName}`
    : content.guestAuthor;

  const authorImage = content?.author?.image
    ? content.author.image.thumbnailUrl
    : null;

  return (
    <div className={classNames(isHero ? className.replace(" content-type-blog", "") : className, {
      "columns is-marginless": isHero,
    })}>
      <div className={classNames("card-header-wrapper", {
        "column hero is-6-tablet is-8-desktop": isHero,
      })}>
        <div className="image-container">
          <LazyLoadImage src={heroImageUrl} alt={heroImageMeta} />
        </div>
        {content?.publishedTimestamp &&
          <div className="card-theme text--label">{DateUtil.formatDateTime(content.publishedTimestamp, "ll")}</div>
        }
      </div>
      <div className={classNames("card-body", {
        "content-type-blog hero column is-6-tablet is-4-desktop": isHero,
      })}>
        <div>
          <h3 className="card-title text--dark">{content.title}</h3>
          {authorImage &&
            <div className="card-subtitle">
              <div className="card-avatar">
                <Avatar source={authorImage} rounded={true} />
              </div>
              <span className="text--label text--mid">{authorName}</span>
            </div>
          }
          <div className="card-copy">
            {content.meta.descriptionTag &&
              <p className="card-description text--regular">{content.meta.descriptionTag}</p>
            }
          </div>
        </div>
        <div className="card-tags">
          {renderTags(content)}
        </div>
      </div>

      <DraggingContext.Consumer>
        {props => <Link
          {...props}
          className="card-link"
          to={url}
          tabIndex={tabIndex || 0}
          onClick={onClick}
        />}
      </DraggingContext.Consumer>
    </div>
  );
};
